import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        body.append(key, value as string);
      }
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d9od5te8mbnljin5abm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络连接失败，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <main className="pt-16">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/70 text-primary-700 rounded-full text-xs font-medium mb-4">
            <i className="ri-building-line"></i>
            关于90影视
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            致力于打造极致的<span className="text-primary-500">高清影视体验</span>
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
            90影视成立于2026年，是一个专注于提供高清超清4K蓝光1080P画质影视资源的在线平台。
            我们汇聚了电影、电视剧、动漫、综艺、纪录片等丰富的影视内容，致力于让每一位用户都能享受到极致的视觉盛宴。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent-100/80 text-accent-700 rounded-full text-xs font-medium mb-4">
                <i className="ri-heart-line"></i>
                品牌故事
              </div>
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">
                让每一个人都能<span className="text-accent-500">免费看好片</span>
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  90影视的创始团队来自互联网视频行业的资深从业者，我们深知高清画质对于观影体验的重要性。
                  在创始之初，我们就立下了"让每个人都能免费享受高清影视"的使命。
                </p>
                <p>
                  通过自研的视频压缩算法和全球CDN加速网络，90影视成功实现了在保证超清画质的同时，
                  大幅降低用户的带宽消耗，让"不缓冲"从一个口号变成了每一位用户的真实体验。
                </p>
                <p>
                  我们的内容团队每日精心筛选和更新优质影视资源，涵盖院线大片、热门剧集、经典动漫、
                  高分纪录片等全品类内容，满足不同用户的观影需求。
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8">
                {[
                  { number: '10万+', label: '影视资源' },
                  { number: '5000万+', label: '月活跃用户' },
                  { number: '4.9', label: '用户评分' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 bg-background-100 rounded-xl">
                    <div className="text-lg md:text-2xl font-heading font-bold text-accent-500 mb-1">{stat.number}</div>
                    <div className="text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20with%20team%20discussing%20around%20large%20monitor%20displaying%20film%20content%2C%20warm%20natural%20light%2C%20collaborative%20atmosphere%2C%20professional%20but%20relaxed%20vibe&width=800&height=600&seq=about-team-01&orientation=landscape"
                alt="90影视团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium mb-4">
              <i className="ri-star-line"></i>
              用户口碑
            </div>
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">用户对我们的评价</h2>
            <p className="text-sm text-foreground-600">来自真实用户的真实反馈</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { name: '张先生', rating: 5, text: '画质真的没话说，4K蓝光资源流畅播放，完全不需要缓冲，比很多付费平台都好用。强烈推荐给喜欢看电影的朋友！', date: '2026-07-28' },
              { name: '李女士', rating: 5, text: '电视剧更新速度非常快，追剧党福音！每天下班回家打开90影视，一天的疲惫都被治愈了。界面也很简洁干净。', date: '2026-07-25' },
              { name: '王同学', rating: 5, text: '作为一个动漫迷，90影视的动漫资源真的很全，而且都是高清画质。国漫和日漫都有，太良心了！', date: '2026-07-22' },
              { name: '赵先生', rating: 4, text: '用了三个月了，资源丰富、画质清晰、播放流畅。建议增加更多的纪录片分类，期待平台越来越好。', date: '2026-07-18' },
              { name: '陈女士', rating: 5, text: '全家都在用90影视，我爸妈喜欢看老电影，孩子爱看动漫，我和老公追剧，一个平台满足全家人需求。', date: '2026-07-15' },
              { name: '刘先生', rating: 5, text: '作为一个影迷，90影视的高分推荐栏目做得很好，帮我发现了很多冷门好片。推荐算法很懂我！', date: '2026-07-12' },
            ].map((review, idx) => (
              <div key={idx} className="p-5 bg-background-50 rounded-xl">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">{review.text}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-900">{review.name}</span>
                  <span className="text-xs text-foreground-500">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100/70 text-primary-700 rounded-full text-xs font-medium mb-4">
                <i className="ri-mail-send-line"></i>
                联系我们
              </div>
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">期待您的来信</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                如果您有任何问题、建议或者合作意向，欢迎随时与我们联系。我们的团队会在24小时内回复您的消息。
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                    <div className="text-sm text-foreground-600">contact@90yingshi.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">客服电话</div>
                    <div className="text-sm text-foreground-600">400-888-9090</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">公司地址</div>
                    <div className="text-sm text-foreground-600">北京市朝阳区影视产业园区B座12层</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <h3 className="font-heading font-semibold text-foreground-950 mb-5">发送消息</h3>

              {formStatus === 'success' && (
                <div className="mb-4 p-4 bg-accent-100/60 border border-accent-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i>
                    <span className="text-sm font-medium text-accent-800">消息已发送成功！我们会尽快回复您。</span>
                  </div>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-4 p-4 bg-primary-100/60 border border-primary-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-fill text-primary-600 text-lg"></i>
                    <span className="text-sm font-medium text-primary-800">{formError || '发送失败，请稍后重试'}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form="true">
                <div className="anti-spam-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容
                      <span className="text-foreground-400 font-normal ml-1">({charCount}/500)</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的消息内容"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于90影视",
            "description": "90影视是一个专注于提供高清超清4K蓝光1080P画质影视资源的在线平台。",
            "url": "https://www.90yingshi.com/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "90影视",
              "url": "https://www.90yingshi.com/"
            }
          })
        }}
      />
    </main>
  );
}